import { FC, SyntheticEvent, useEffect, useState } from "react";
import { Box, Drawer } from "@mui/material";
import {
  buttons,
  carModelsMenu,
  languageMenuHeight,
  languages,
} from "../hardCodeData";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { changeLanguage } from "../../../utils/languageUtils";
import LanguageAwareNavLink from "../../../components/LanguageAwareNavLink";

interface props {
  openMobileMenu: boolean;
  toggleDrawer: (newOpen: boolean) => () => void;
}

const MobileMenu: FC<props> = ({ openMobileMenu, toggleDrawer }) => {
  const { t, i18n } = useTranslation();
  const pathName = useLocation();
  const [expanded, setExpanded] = useState<string | false>(false);
  const [activeCategory, setActiveCategory] = useState<string>("electric");

  useEffect(() => {
    if (!openMobileMenu) {
      setExpanded(false);
      setActiveCategory("electric");
    }
  }, [openMobileMenu]);

  const handleChange =
    (panel: string) => (event: SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleLanguageChange = (lng: string) => {
    // Обновляем localStorage и URL
    changeLanguage(lng);
    // Меняем язык в i18n
    i18n.changeLanguage(lng);
  };

  // Фильтруем автомобили по активной категории
  const filteredCars = carModelsMenu.filter(car => car.type === activeCategory);

  // Получаем уникальные категории
  const categories = Array.from(new Set(carModelsMenu.map(car => car.type)));

  return (
    <Drawer
      anchor="left"
      open={openMobileMenu}
      onClose={toggleDrawer(!openMobileMenu)}
    >
      <Box
        sx={{
          width: "100vw",
          height: "calc(100vh - 60px)",
          maxHeight: "calc(100vh - 60px)",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            pb: 3,
          }}
        >
          {buttons.map((button, i) => {
            const models = button.name === "BYD";
            if (models) {
              return (
                <Accordion
                  key={i}
                  expanded={expanded === "panel1"}
                  onChange={handleChange("panel1")}
                  aria-controls="panel1bh-content"
                  id="panel1bh-header"
                  sx={{
                    boxShadow: "none",
                    mb: "-16px",
                    mt: 1,
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "18px",
                        fontFamily: "Montserrat",
                      }}
                    >
                      {t(`common:${button.name}`)}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    {/* Категории */}
                    <Box
                      sx={{
                        position: "sticky",
                        top: 0,
                        py: 1,
                        backgroundColor: "#fff",
                        zIndex: 10,
                        display: "flex",
                        gap: 1,
                        mb: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      {categories.map((category) => (
                        <Box
                          key={category}
                          onClick={() => setActiveCategory(category)}
                          sx={{
                            color: activeCategory === category ? "#000" : "#555",
                            fontSize: "16px",
                            fontWeight: 500,
                            textDecoration: activeCategory === category ? "underline" : "none",
                          }}
                        >
                          {t(`common:${category === "electric" ? "electric_vehicles" : "hybrid"}`)}
                        </Box>
                      ))}
                    </Box>

                    {/* Автомобили выбранной категории */}
                    <Box
                      sx={{
                        display: "flex",
                        maxWidth: "100%",
                        overflowX: "auto",
                        scrollbarWidth: "none",
                        "&::-webkit-scrollbar": {
                          display: "none",
                        },
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: "center",
                      }}
                    >
                      {filteredCars.map((car, i) => (
                        <LanguageAwareNavLink
                          key={i}
                          style={{ textDecoration: "none", color: "inherit" }}
                          to={`/Explore?carId=${car.id}`}
                          onClick={toggleDrawer(!openMobileMenu)}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              height: "250px",
                              width: "300px",
                              transition: "ease-in-out 0.25s",
                              mx: 2,
                            }}
                          >
                            <Box
                              sx={{
                                my: 3,
                                fontSize: "20px",
                                fontWeight: 500,
                                userSelect: "none",
                              }}
                            >
                              {car.name}
                            </Box>
                            <img alt="car" src={car.img} height={"60%"} />
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: "calc(100% - 32px)",
                                px: 2,
                                mt: 3,
                              }}
                            >
                            </Box>
                          </Box>
                        </LanguageAwareNavLink>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            }
            return (
              <LanguageAwareNavLink
                key={i}
                className="NavButtonsMobile"
                to={models ? pathName.pathname : button.path}
                onClick={toggleDrawer(!openMobileMenu)}
              >
                {t(`common:${button.name}`)}
              </LanguageAwareNavLink>
            );
          })}
        </Box>
        <Accordion
          expanded={expanded === "panel2"}
          onChange={handleChange("panel2")}
          aria-controls="panel2bh-content"
          id="panel2bh-header"
          sx={{
            boxShadow: "none",
            mb: "-16px",
            mt: 1,
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "18px",
                fontFamily: "Montserrat",
              }}
            >
              {t(`common:language`)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            {languages.map((language, i) => (
              <Box
                key={i}
                onClick={() => handleLanguageChange(language.lng)}
                sx={{
                  px: { xs: 0, sm: 2 },
                  display: "flex",
                  alignItems: "center",
                  height: languageMenuHeight,
                  width: "fit-content",
                  fontWeight: 600,
                  fontSize: "14px",
                  color:
                    i18n.resolvedLanguage === language.lng ? "#000" : "#555",
                  transition: "ease-in-out 0.1s",
                  "&:hover": {
                    color: "#000",
                    cursor: "pointer",
                  },
                }}
              >
                <Box
                  onClick={toggleDrawer(!openMobileMenu)}
                  sx={{
                    transition: "ease-in-out 0.25s",
                    borderBottom: `1px rgba(0,0,0,${i18n.resolvedLanguage === language.lng ? 1 : 0
                      }) solid`,
                  }}
                >
                  {language.name}
                </Box>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      </Box>
    </Drawer>
  );
};

export default MobileMenu;
